package com.isaias.fyattravel.web

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.browser.localStorage
import org.w3c.dom.events.Event
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Headers
import org.jetbrains.compose.web.renderComposable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.attributes.InputType
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.shared.deeplink.BookingLinks
import com.isaias.fyattravel.web.navigation.currentRoute
import com.isaias.fyattravel.web.navigation.parsePath
import com.isaias.fyattravel.web.navigation.parseQueryParam
import com.isaias.fyattravel.web.navigation.navigateTo
import com.isaias.fyattravel.web.layout.Sidebar
import com.isaias.fyattravel.web.layout.LanguageFab
import com.isaias.fyattravel.web.layout.PageHeaderRow
import com.isaias.fyattravel.web.layout.PlaceholderPage
import com.isaias.fyattravel.web.layout.Footer
import com.isaias.fyattravel.web.auth.AuthManager
import com.isaias.fyattravel.web.auth.WebAuthPage
import com.isaias.fyattravel.web.auth.UserProfilePage
import com.isaias.fyattravel.web.auth.rememberAuthState
import com.isaias.fyattravel.web.visa.VisaPage
import com.isaias.fyattravel.web.pages.home.HomePage
import com.isaias.fyattravel.web.pages.flights.FlightsPage
import com.isaias.fyattravel.web.pages.checkout.CheckoutPage
import com.isaias.fyattravel.web.pages.payment.PaymentSuccessPage
import com.isaias.fyattravel.web.pages.payment.PaymentFailedPage
import com.isaias.fyattravel.web.pages.contact.ContactPage
import com.isaias.fyattravel.web.pages.explore.ExplorePage
import com.isaias.fyattravel.web.pages.explore.PackagesSection
import com.isaias.fyattravel.web.pages.settings.LanguagePage
import com.isaias.fyattravel.web.layout.ResponsiveStyles
import com.isaias.fyattravel.web.home.DynamicHeroSection
import com.isaias.fyattravel.web.flight.EnhancedFlightSearchCard
import com.isaias.fyattravel.web.components.ServiceTabs
import com.isaias.fyattravel.web.components.OffersSection
import com.isaias.fyattravel.web.components.HotelSearchCard
import com.isaias.fyattravel.web.components.CarRentalSearchCard
import com.isaias.fyattravel.web.data.WebProduct
import com.isaias.fyattravel.web.data.WebFlight
import com.isaias.fyattravel.web.data.WebHotel
import com.isaias.fyattravel.web.data.WebCarRental
import com.isaias.fyattravel.web.data.WebCategory
import com.isaias.fyattravel.web.theme.ThemeColors

private const val FUNCTIONS_BASE = "https://us-central1-fyattravelnew.cloudfunctions.net"

object AppStyles : StyleSheet() {
    init {
        "html, body, #root" style {
            height(100.percent)
            margin(0.px)
            padding(0.px)
            property("overflow-x", "hidden")
        }
    }
}

fun main() {
    renderComposable(rootElementId = "root") {
        Style(AppStyles)
        Style(ResponsiveStyles)
        WebApp()
    }
}

@Composable
fun WebApp() {
    val routeState = remember { mutableStateOf(currentRoute()) }
    val localeState = WebLocaleStore.locale.value
    
    // Initialize Auth Manager
    LaunchedEffect(Unit) {
        AuthManager.initialize()
    }
 
    // Apply locale + direction to document
    LaunchedEffect(localeState) {
        WebLocaleStore.applyDocumentLocale(localeState)
    }

    // Default route - start on flights page
    LaunchedEffect(Unit) {
        if (window.location.hash.isBlank()) {
            window.location.hash = "/flights"
        }
        routeState.value = currentRoute()
    }

    // Listen to hash changes
    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = {
            routeState.value = currentRoute()
        }
        window.addEventListener("hashchange", listener)
        onDispose { window.removeEventListener("hashchange", listener) }
    }

    Div({
        classes(ResponsiveStyles.rootMobileColumn)
            style {
                display(DisplayStyle.Flex)
                minHeight(100.vh)
                backgroundColor(ThemeColors.background)
                property("direction", if (WebLocaleStore.isRtl(localeState)) "rtl" else "ltr")
                fontFamily(
                "system-ui",
                "-apple-system",
                "BlinkMacSystemFont",
                "Segoe UI",
                "Roboto",
                "sans-serif"
            )
        }
    }) {
        Sidebar(
            route = routeState.value,
            onNavigate = ::navigateTo
        )
        MainContent(
            route = routeState.value,
            onNavigate = ::navigateTo
        )
        LanguageFab()
    }
}

private fun currentRoute(): String {
    val raw = window.location.hash.orEmpty()
    // "#/flights" -> "/flights"
    val trimmed = raw.removePrefix("#")
    return if (trimmed.isBlank()) "/" else trimmed
}

private fun navigateTo(route: String) {
    val normalized = if (route.startsWith("/")) route else "/$route"
    window.location.hash = normalized
}

private fun parsePath(route: String): String = route.substringBefore("?").trim().ifEmpty { "/" }
private fun parseQueryParam(route: String, key: String): String? {
    val query = route.substringAfter("?", missingDelimiterValue = "")
    if (query.isBlank()) return null
    val pairs = query.split("&").mapNotNull { part ->
        val idx = part.indexOf("=")
        if (idx <= 0) return@mapNotNull null
        val k = part.substring(0, idx)
        val v = part.substring(idx + 1)
        k to v
    }
    val raw = pairs.firstOrNull { it.first == key }?.second ?: return null
    return try {
        js("decodeURIComponent")(raw) as String
    } catch (_: dynamic) {
        raw
    }
}

private fun encodeQuery(value: String): String = try {
    js("encodeURIComponent")(value) as String
} catch (_: dynamic) {
    value
}

@Composable
private fun PageHeaderRow(
    title: String,
    lastUpdatedMs: Double?,
    onRefresh: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            marginTop(0.5.em)
            marginBottom(1.em)
        }
    }) {
        H1({
            style {
                fontSize(2.0.em)
                fontWeight("900")
                margin(0.px)
                color(ThemeColors.textPrimary)
                property("@media (max-width: 640px)", "{ font-size: 1.4em; }")
            }
        }) { Text(title) }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(0.75.em)
            }
        }) {
                if (lastUpdatedMs != null) {
                val label = try {
                    js("new Date(lastUpdatedMs).toLocaleTimeString()") as String
                } catch (_: dynamic) {
                    ""
                }
                if (label.isNotBlank()) {
                        Span({ style { color(ThemeColors.textSecondary); fontSize(0.9.em) } }) {
                            Text(WebStrings.format(WebStringKey.UPDATED_AT, label))
                        }
                }
            }

            Button(attrs = {
                style {
                    backgroundColor(ThemeColors.surface)
                    color(ThemeColors.textPrimary)
                    padding(0.6.em, 0.95.em)
                    borderRadius(0.75.em)
                    property("border", "1px solid ${ThemeColors.border}")
                    cursor("pointer")
                    fontWeight("800")
                }
                onClick { onRefresh() }
            }) { Text(WebStrings.t(WebStringKey.REFRESH)) }
        }
    }
}

/* ---------------- MAIN CONTENT ---------------- */

@Composable
fun MainContent(
    route: String,
    onNavigate: (String) -> Unit,
) {
    val path = parsePath(route)
    val searchQ = parseQueryParam(route, "q").orEmpty()

    Div({
        style {
            flex(1)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
               property("min-height", "0")
            property("overflow-x", "hidden")
            property("overflow-y", "auto")
        }
    }) {
        // Content wrapper - expands to push footer to bottom on short pages
        Div({
            style {
                flex(1)
                padding(2.em)
                property("@media (max-width: 1024px)", "{ padding: 1.25em; }")
                property("@media (max-width: 768px)", "{ padding: 0.9em; }")
            }
        }) {
            Div({
                style {
                    maxWidth(1200.px)
                    width(100.percent)
                    property("margin", "0 auto")
                }
            }) {
                when (path) {
                    "/" -> HomePage(onNavigate = onNavigate)
                "/flights" -> FlightsPage(
                    onNavigate = onNavigate,
                    openEnquiry = parseQueryParam(route, "enquiry") == "true"
                )
                "/stays" -> StaysPage()
                "/cars" -> CarsPage()
                "/visa_logo" -> VisaPage()
                "/categories" -> CategoriesPage(onNavigate = onNavigate)
                "/explore" -> ExplorePage()
                "/cart" -> PlaceholderPage(
                    title = WebStrings.t(WebStringKey.CART_LABEL),
                    subtitle = WebStrings.t(WebStringKey.CART_COMING_SOON)
                )
                "/profile" -> UserProfilePage(onNavigateHome = { onNavigate("/") })
                "/auth" -> WebAuthPage(onAuthSuccess = { onNavigate("/") })
                "/contact" -> ContactPage()
                "/language" -> LanguagePage()
                "/admin" -> PlaceholderPage(
                    title = WebStrings.t(WebStringKey.ADMIN),
                    subtitle = WebStrings.t(WebStringKey.ADMIN_COMING_SOON)
                )
                else -> {
                    if (path.startsWith("/category/")) {
                        val rawName = path.removePrefix("/category/").trim()
                        val name = js("decodeURIComponent(rawName)") as String
                        CategoryProductsPage(categoryName = name, onNavigate = onNavigate)
                    } else if (path.startsWith("/product/")) {
                        val id = path.removePrefix("/product/").trim()
                        ProductDetailsPage(productId = id, onNavigate = onNavigate)
                    } else if (path == "/checkout") {
                        val productId = parseQueryParam(route, "productId").orEmpty()
                        CheckoutPage(productId = productId, onNavigate = onNavigate)
                    } else if (path == "/payment/success") {
                        PaymentSuccessPage(onNavigate = onNavigate)
                    } else if (path == "/payment/failed") {
                        PaymentFailedPage(onNavigate = onNavigate)
                    } else if (path == "/search") {
                        SearchPage(query = searchQ, onNavigate = onNavigate)
                    } else {
                        PlaceholderPage(
                            title = WebStrings.t(WebStringKey.NOT_FOUND),
                            subtitle = WebStrings.format(WebStringKey.UNKNOWN_ROUTE, route)
                        )
                    }
                }
                }
            }
        }

        // Footer - never shrinks, always at bottom
        Div({
            style {
                property("flex-shrink", "0")
                property("margin-top", "0px")
            }
        }) {
            Footer()
        }
    } 
}

/* ---------------- SEARCH ---------------- */

@Composable
private fun SearchPage(query: String, onNavigate: (String) -> Unit) {
    val q = query.trim()
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var products by remember { mutableStateOf(emptyList<WebProduct>()) }
    var lastUpdatedMs by remember { mutableStateOf<Double?>(null) }
    var inFlight by remember { mutableStateOf(false) }
    var refreshTick by remember { mutableStateOf(0) }
    val locale = WebLocaleStore.locale.value

    fun refresh(showSpinner: Boolean) {
        if (inFlight) return
        inFlight = true
        if (showSpinner) loading = true
        error = null

        // Empty query: show empty results without hitting backend
        if (q.isBlank()) {
            products = emptyList()
            loading = false
            lastUpdatedMs = js("Date.now()") as Double
            inFlight = false
            return
        }

        val bodyJson = buildString {
            append("{")
            append("\"query\":\"${q}\",")
            append("\"limit\":50")
            append("}")
        }

        window.fetch(
            "$FUNCTIONS_BASE/searchProducts",
            RequestInit(
                method = "POST",
                headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                body = bodyJson
            )
        ).then { resp ->
            resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val ok = (obj.success as Boolean?) == true
                    if (!ok) {
                        error = (obj.message as String?) ?: "Search failed"
                    } else {
                        val arr = (obj.products as Array<dynamic>?) ?: emptyArray()
                        products = arr.map { d ->
                            WebProduct(
                                id = d.id as String? ?: "",
                                title = d.title as String?,
                                description = d.description as String?,
                                thumbnail = d.thumbnail as String?,
                                category = d.category as String?,
                                createdAt = (d.createdAt as Double?)?.toLong(),
                                priceUsd = (d.priceUsd as Double?)?.toInt(),
                                priceUgx = (d.priceUgx as Double?)?.toInt(),
                            )
                        }.filter { it.id.isNotBlank() }
                    }
                } catch (t: dynamic) {
                    error = "Invalid response from server"
                } finally {
                    loading = false
                    lastUpdatedMs = js("Date.now()") as Double
                    inFlight = false
                }
            }
        }.catch { _ ->
            error = "Network error (is the Cloud Function deployed?)"
            loading = false
            lastUpdatedMs = js("Date.now()") as Double
            inFlight = false
        }
    }

    // initial load + manual refresh
    LaunchedEffect(q, refreshTick) {
        refresh(showSpinner = true)
    }

    // polling (best-effort realtime)
    DisposableEffect(q) {
        val intervalId = window.setInterval({
            refresh(showSpinner = false)
        }, 15_000)
        onDispose { window.clearInterval(intervalId) }
    }

    PageHeaderRow(
        title = if (q.isBlank()) {
            WebStrings.t(WebStringKey.FLIGHT_SEARCH)
        } else {
            WebStrings.format(WebStringKey.SEARCH_WITH_QUERY, q)
        },
        lastUpdatedMs = lastUpdatedMs,
        onRefresh = { refreshTick++ }
    )

    if (loading) {
        P({ style { color(ThemeColors.textSecondary); fontWeight("700") } }) { Text("Searching…") }
        return
    }
    error?.let { msg ->
        P({ style { color(ThemeColors.error); fontWeight("700") } }) { Text(msg) }
        return
    }

    if (products.isEmpty()) {
        P({ style { color(ThemeColors.textSecondary) } }) { Text("No results.") }
        return
    }

    ProductGrid(products = products, onNavigate = onNavigate)
}

/* ---------------- PRODUCT DETAILS ---------------- */

@Composable
private fun ProductDetailsPage(productId: String, onNavigate: (String) -> Unit) {
    val pid = productId.trim()
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var product by remember { mutableStateOf<WebProduct?>(null) }
    var category by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(pid) {
        if (pid.isBlank()) return@LaunchedEffect
        loading = true
        error = null
        product = null

        val bodyJson = buildString {
            append("{")
            append("\"id\":\"${pid}\"")
            append("}")
        }

        window.fetch(
            "$FUNCTIONS_BASE/getProductById",
            RequestInit(
                method = "POST",
                headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                body = bodyJson
            )
        ).then { resp ->
            resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val ok = (obj.success as Boolean?) == true
                    if (!ok) {
                        error = (obj.message as String?) ?: "Failed to load product"
                    } else {
                        val d = obj.product
                        category = d.category as String?
                        product = WebProduct(
                            id = d.id as String? ?: pid,
                            title = d.title as String?,
                            description = d.description as String?,
                            thumbnail = d.thumbnail as String?,
                            category = d.category as String?,
                            createdAt = (d.createdAt as Double?)?.toLong(),
                            priceUsd = (d.priceUsd as Double?)?.toInt(),
                            priceUgx = (d.priceUgx as Double?)?.toInt(),
                        )
                    }
                } catch (t: dynamic) {
                    error = "Invalid response from server"
                } finally {
                    loading = false
                }
            }
        }.catch { _ ->
            error = "Network error (is the Cloud Function deployed?)"
            loading = false
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.75.em)
            marginBottom(1.em)
        }
    }) {
        Button({
            style {
                backgroundColor(ThemeColors.surface)
                color(ThemeColors.textPrimary)
                padding(0.65.em, 1.em)
                borderRadius(0.75.em)
                property("border", "1px solid ${ThemeColors.border}")
                cursor("pointer")
                fontWeight("800")
            }
            onClick {
                val cat = category?.trim().orEmpty()
                if (cat.isNotBlank()) onNavigate("/category/$cat") else onNavigate("/categories")
            }
        }) { Text("Back") }
    }

    if (loading) {
        // Loading spinner only, no text
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                padding(3.em)
            }
        }) {
            Div({
                style {
                    width(40.px)
                    height(40.px)
                    borderRadius(50.percent)
                    property("border", "4px solid #333333")
                    property("border-top-color", "#AE7930")
                    property("animation", "spin 1s linear infinite")
                }
            }) {}
        }
        return
    }
    error?.let { msg ->
        P({ style { color(ThemeColors.error); fontWeight("700") } }) { Text(msg) }
        return
    }

    val p = product
    if (p == null) {
        P({ style { color(ThemeColors.textSecondary) } }) { Text("Product not found.") }
        return
    }

    // Compact centered layout
    Div({
        style {
            maxWidth(680.px)
            property("margin", "0 auto")
        }
    }) {
        // Image card
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                borderRadius(1.em)
                padding(1.em)
                marginBottom(1.25.em)
                property("border", "1px solid ${ThemeColors.border}")
                property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
            }
        }) {
            val img = p.thumbnail.orEmpty()
            if (img.isNotBlank()) {
                Img(src = img, attrs = {
                    style {
                        width(100.percent)
                        borderRadius(0.9.em)
                        property("object-fit", "cover")
                        property("max-height", "320px")
                    }
                })
            } else {
                Div({ style { color(ThemeColors.textSecondary); padding(2.em); textAlign("center") } }) { Text("No image") }
            }
        }

        // Details card
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                borderRadius(1.em)
                padding(1.25.em)
                property("border", "1px solid ${ThemeColors.border}")
                property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
            }
        }) {
            // Title
            H1({
                style {
                    margin(0.px)
                    fontSize(1.75.em)
                    fontWeight("800")
                    color(ThemeColors.textPrimary)
                    marginBottom(0.25.em)
                }
            }) { Text(p.title ?: "Item") }

            // Category badge
            Div({
                style {
                    display(DisplayStyle.InlineBlock)
                    backgroundColor(ThemeColors.primaryMuted)
                    color(ThemeColors.primary)
                    padding(0.375.em, 0.75.em)
                    borderRadius(0.5.em)
                    fontSize(0.85.em)
                    fontWeight("600")
                    marginBottom(1.em)
                }
            }) {
                Text(categoryTitleFromName(p.category ?: ""))
            }

            // Description
            val desc = p.description.orEmpty().trim()
            if (desc.isNotBlank()) {
                P({ 
                    style { 
                        color(ThemeColors.textSecondary)
                        lineHeight("1.6")
                        marginBottom(1.25.em)
                    } 
                }) { Text(desc) }
            }

            // Price
            val priceParts = listOfNotNull(
                p.priceUsd?.let { "USD $it" },
                p.priceUgx?.let { "UGX $it" }
            )
            if (priceParts.isNotEmpty()) {
                Div({
                    style {
                        backgroundColor(ThemeColors.successBg)
                        padding(1.em)
                        borderRadius(0.75.em)
                        marginBottom(1.25.em)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.SpaceBetween)
                    }
                }) {
                    Span({
                        style {
                            color(ThemeColors.success)
                            fontWeight("600")
                            fontSize(0.9.em)
                        }
                    }) { Text("Price") }
                    Span({
                        style {
                            color(ThemeColors.success)
                            fontWeight("800")
                            fontSize(1.35.em)
                        }
                    }) { Text(priceParts.joinToString(" • ")) }
                }
            }

            // Book Now button
            Button({
                style {
                    width(100.percent)
                    backgroundColor(ThemeColors.primary)
                    color(ThemeColors.textOnGold)
                    padding(1.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    fontSize(1.1.em)
                    property("box-shadow", "0 4px 12px rgba(37,99,235,0.3)")
                    property("transition", "all 0.15s ease")
                }
                onClick { onNavigate("/checkout?productId=${p.id}") }
            }) {
                Text("Book Now")
            }
        }
    }
}

/* ---------------- PRODUCT GRID + CARDS ---------------- */

@Composable
private fun ProductGrid(products: List<WebProduct>, onNavigate: (String) -> Unit) {
    Div({
        style {
            display(DisplayStyle.Grid)
            gridTemplateColumns("repeat(auto-fit, minmax(260px, 1fr))")
            gap(1.em)
        }
    }) {
        products.forEach { p ->
            ProductCard(product = p, onClick = { onNavigate("/product/${p.id}") })
        }
    }
}

@Composable
private fun ProductCard(product: WebProduct, onClick: () -> Unit) {
    Div({
        style {
            backgroundColor(ThemeColors.surface)
            padding(1.em)
            borderRadius(0.9.em)
            property("border", "1px solid ${ThemeColors.border}")
            property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
            cursor("pointer")
        }
        onClick { onClick() }
    }) {
        val img = product.thumbnail.orEmpty()
        if (img.isNotBlank()) {
            Img(src = img, attrs = {
                style {
                    width(100.percent)
                    borderRadius(0.75.em)
                    property("object-fit", "cover")
                    property("max-height", "170px")
                    marginBottom(0.75.em)
                }
            })
        }
        H3({ style { marginBottom(0.25.em); color(ThemeColors.textPrimary) } }) {
            Text(product.title ?: "Item")
        }
        val desc = product.description.orEmpty().trim()
        if (desc.isNotBlank()) {
            P({ style { color(ThemeColors.textSecondary) } }) {
                Text(desc.take(120) + if (desc.length > 120) "…" else "")
            }
        }
        val priceParts = listOfNotNull(
            product.priceUsd?.let { "USD $it" },
            product.priceUgx?.let { "UGX $it" }
        )
        if (priceParts.isNotEmpty()) {
            P({ style { marginTop(0.5.em); color(ThemeColors.primary); fontWeight("900") } }) {
                Text(priceParts.joinToString(" • "))
            }
        }
    }
}
@Composable
private fun FlightsPage(onNavigate: (String) -> Unit) {
    DynamicHeroSection(
        onSearchFlights = { onNavigate("/flights") },
        onSearchHotels = { onNavigate("/stays") }
    )
    ServiceTabs(activeRoute = "/flights", onNavigate = onNavigate)
    EnhancedFlightSearchCard { params ->
        // Handle search - could trigger actual search here
        console.log("Searching flights:", params)
    }
    OffersSection()
}

@Composable
private fun StaysPage() {
    H1({
        style {
            fontSize(2.2.em)
            fontWeight("800")
            marginBottom(0.4.em)
            color(Color("#ffffff"))
        }
    }) { Text(WebStrings.t(WebStringKey.EXPLORE_STAYS)) }

    P({
        style {
            fontSize(1.05.em)
            color(Color("#a0a0a0"))
            marginBottom(1.25.em)
        }
    }) { Text(WebStrings.t(WebStringKey.STAYS_PAGE_SUBTITLE)) }

    HotelSearchCard()
}

@Composable
private fun CarsPage() {
    H1({
        style {
            fontSize(2.2.em)
            fontWeight("800")
            marginBottom(0.4.em)
            color(Color("#ffffff"))
        }
    }) { Text(WebStrings.t(WebStringKey.CAR_RENTAL)) }

    P({
        style {
            fontSize(1.05.em)
            color(Color("#a0a0a0"))
            marginBottom(1.25.em)
        }
    }) { Text(WebStrings.t(WebStringKey.CARS_PAGE_SUBTITLE)) }

    CarRentalSearchCard()
}

/* ---------------- CATEGORIES (FIRESTORE via Cloud Functions) ---------------- */

@Composable
private fun CategoriesPage(onNavigate: (String) -> Unit) {
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var categories by remember { mutableStateOf(emptyList<WebCategory>()) }
    var lastUpdatedMs by remember { mutableStateOf<Double?>(null) }
    var inFlight by remember { mutableStateOf(false) }
    var refreshTick by remember { mutableStateOf(0) }

    // Inline expansion state
    var expandedCategory by remember { mutableStateOf<String?>(null) }
    var expandedProducts by remember { mutableStateOf(emptyList<WebProduct>()) }
    var expandedLoading by remember { mutableStateOf(false) }
    var expandedError by remember { mutableStateOf<String?>(null) }
    
    // Filter state
    var selectedFilter by remember { mutableStateOf("All") }

    fun refresh(showSpinner: Boolean) {
        if (inFlight) return
        inFlight = true
        if (showSpinner) loading = true
        error = null

        window.fetch("$FUNCTIONS_BASE/listProductCategories").then { resp ->
            resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val ok = (obj.success as Boolean?) == true
                    if (!ok) {
                        error = (obj.message as String?) ?: "Failed to load categories"
                    } else {
                        val arr = (obj.categories as Array<dynamic>?) ?: emptyArray()
                        val next = arr.map { d ->
                            WebCategory(
                                name = d.name as String? ?: "",
                                count = (d.count as Int?) ?: ((d.count as Double?)?.toInt() ?: 0)
                            )
                        }.filter { it.name.isNotBlank() }

                        // Avoid unnecessary state updates if unchanged
                        val oldKey = categories.joinToString("|") { "${it.name}:${it.count}" }
                        val newKey = next.joinToString("|") { "${it.name}:${it.count}" }
                        if (oldKey != newKey) {
                            categories = next
                        }
                    }
                } catch (t: dynamic) {
                    error = "Invalid response from server"
                } finally {
                    loading = false
                    lastUpdatedMs = js("Date.now()") as Double
                    inFlight = false
                }
            }
        }.catch { err ->
            val errorMessage = err.message as? String ?: ""
            if (errorMessage.contains("FAILED_PRECONDITION") || errorMessage.contains("index")) {
                error = "Database index missing. Please ask the admin to create a Firestore index for 'product' collection (category ascending, createdAt descending)."
            } else {
                error = "Network error (is the Cloud Function deployed?)"
            }
            loading = false
            lastUpdatedMs = js("Date.now()") as Double
            inFlight = false
        }
    }

    LaunchedEffect(Unit, refreshTick) {
        refresh(showSpinner = true)
    }

    fun fetchProductsForCategory(categoryName: String) {
        expandedLoading = true
        expandedError = null
        expandedProducts = emptyList()

        val canonical = canonicalCategoryKey(categoryName)
        val variations = listOfNotNull(
            categoryName,
            categoryName.replaceFirstChar { it.uppercase() },
            categoryName.lowercase(),
            canonical.replaceFirstChar { it.uppercase() },
            canonical.lowercase(),
            if (canonical == "flight") "flightOffers" else null,
            if (canonical == "hotel") "Hotel" else null,
            if (canonical == "hotel") "hotel" else null,
            if (canonical == "car") "Car Rental" else null,
            if (canonical == "car") "car_rental" else null,
        ).distinct()

        fun tryNext(remaining: List<String>) {
            if (remaining.isEmpty()) {
                expandedLoading = false
                return
            }
            val head = remaining.first()
            val tail = remaining.drop(1)

            val bodyJson = buildString {
                append("{")
                append("\"category\":\"${head.trim()}\",")
                append("\"limit\":50")
                append("}")
            }

            window.fetch(
                "$FUNCTIONS_BASE/listProductsByCategory",
                RequestInit(
                    method = "POST",
                    headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                    body = bodyJson
                )
            ).then { resp ->
                resp.text().then { text ->
                    try {
                        val obj = kotlin.js.JSON.parse<dynamic>(text)
                        val ok = (obj.success as Boolean?) == true
                        if (!ok) {
                            if (tail.isNotEmpty()) {
                                tryNext(tail)
                            } else {
                                expandedError = (obj.message as String?) ?: "Failed to load products"
                                expandedLoading = false
                            }
                        } else {
                            val arr = (obj.products as Array<dynamic>?) ?: emptyArray()
                            val next = arr.map { d ->
                                WebProduct(
                                    id = d.id as String? ?: "",
                                    title = d.title as String?,
                                    description = d.description as String?,
                                    thumbnail = d.thumbnail as String?,
                                    category = d.category as String?,
                                    createdAt = (d.createdAt as Double?)?.toLong(),
                                    priceUsd = (d.priceUsd as Double?)?.toInt(),
                                    priceUgx = (d.priceUgx as Double?)?.toInt(),
                                )
                            }.filter { it.id.isNotBlank() }

                            if (next.isNotEmpty()) {
                                expandedProducts = next
                                expandedLoading = false
                            } else if (tail.isNotEmpty()) {
                                tryNext(tail)
                            } else {
                                expandedLoading = false
                            }
                        }
                    } catch (t: dynamic) {
                        if (tail.isNotEmpty()) {
                            tryNext(tail)
                        } else {
                            expandedError = "Invalid response from server"
                            expandedLoading = false
                        }
                    }
                }
            }.catch { _ ->
                if (tail.isNotEmpty()) {
                    tryNext(tail)
                } else {
                    expandedError = "Network error (is the Cloud Function deployed?)"
                    expandedLoading = false
                }
            }
        }

        tryNext(variations)
    }

    // Filter chips data
    val filterOptions = listOf(
        "All" to WebStrings.t(WebStringKey.CATEGORIES_FILTER_ALL),
        "Flight" to WebStrings.t(WebStringKey.CATEGORIES_FILTER_FLIGHTS),
        "Hotel" to WebStrings.t(WebStringKey.CATEGORIES_FILTER_HOTELS),
        "Car" to WebStrings.t(WebStringKey.CATEGORIES_FILTER_CAR_RENTALS),
        "Tour" to WebStrings.t(WebStringKey.CATEGORIES_FILTER_TOURS),
        "Transfer" to WebStrings.t(WebStringKey.CATEGORIES_FILTER_TRANSFERS)
    )
    
    // Category type detection for icons and colors
    fun getCategoryType(name: String): String {
        return canonicalCategoryKey(name)
    }
    
    // Category SVG icons (20x20, stroke-based)
    fun getCategoryIconSvg(type: String): String {
        return when (type) {
            "flight" -> """<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M21 16l-4-4 1.9-4.8A1 1 0 0018 6h0a1 1 0 00-.8.4L13 12H8l-2 2h4l1 3h2l1-2h3.2l1.3 1.3a1 1 0 001.5-1.3z"/><line x1="3" y1="20" x2="21" y2="20"/></svg>"""
            "hotel" -> """<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m16 0H5m16 0h2m-2-6H5m6 0V9m0 6v6"/></svg>"""
            "car" -> """<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M5 17H3a2 2 0 01-2-2V9a2 2 0 012-2h16a2 2 0 012 2v6a2 2 0 01-2 2h-2"/><rect x="7" y="14" width="10" height="6" rx="1"/><circle cx="7.5" cy="17.5" r="1.5"/><circle cx="16.5" cy="17.5" r="1.5"/></svg>"""
            "visa" -> """<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="14" x2="6" y2="14.01"/><line x1="10" y1="14" x2="10" y2="14.01"/></svg>"""
            "tour" -> """<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>"""
            "transfer" -> """<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M6 18v2"/><path d="M18 18v2"/><circle cx="7" cy="12" r="1"/><circle cx="17" cy="12" r="1"/></svg>"""
            else -> """<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>"""
        }
    }
    
    fun getCategoryColor(type: String): String {
        return when (type) {
            "flight" -> "#AE7930"
            "hotel" -> "#0891b2"
            "car" -> "#059669"
            "visa" -> "#7c3aed"
            "tour" -> "#dc2626"
            "transfer" -> "#ea580c"
            else -> "#6b7280"
        }
    }

    // Filter categories based on selected filter chip
    val filteredCategories = categories.filter { cat ->
        selectedFilter == "All" ||
            canonicalCategoryKey(cat.name) == canonicalCategoryKey(selectedFilter)
    }

    PageHeaderRow(
        title = WebStrings.t(WebStringKey.CATEGORIES),
        lastUpdatedMs = lastUpdatedMs,
        onRefresh = { refreshTick++ }
    )

    // Filter chips
    Div({
        style {
            display(DisplayStyle.Flex)
            flexWrap(FlexWrap.Wrap)
            gap(0.5.em)
            marginBottom(1.25.em)
        }
    }) {
        filterOptions.forEach { (key, label) ->
            val isSelected = selectedFilter == key
            Button({
                style {
                    backgroundColor(if (isSelected) Color("#AE7930") else Color("#000000"))
                    color(if (isSelected) Color.white else Color("#a0a0a0"))
                    padding(0.5.em, 0.875.em)
                    borderRadius(2.em)
                    border(0.px)
                    property("border", if (isSelected) "none" else "1px solid #333333")
                    cursor("pointer")
                    fontWeight(if (isSelected) "600" else "500")
                    fontSize(0.9.em)
                    property("transition", "all 0.15s ease")
                }
                onClick { selectedFilter = key }
            }) {
                Text(label)
            }
        }
    }

    // Transfer enquiry form (shown instead of category grid)
    if (selectedFilter == "Transfer") {
        TransferEnquiryForm()
        return
    }

    // Uganda safari packages (shown instead of category grid — same static packages as the Explore page)
    if (selectedFilter == "Tour") {
        PackagesSection()
        return
    }

    if (loading) {
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("repeat(auto-fit, minmax(260px, 1fr))")
                gap(1.em)
            }
        }) {
            // Skeleton loaders
            repeat(6) {
                Div({
                    style {
                        backgroundColor(Color("#222222"))
                        padding(1.25.em)
                        borderRadius(1.em)
                        property("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
                    }
                }) {
                    Div({ style { height(48.px); width(48.px); backgroundColor(Color("#333333")); borderRadius(0.75.em); marginBottom(0.75.em) } }) {}
                    Div({ style { height(20.px); width(70.percent); backgroundColor(Color("#333333")); borderRadius(0.375.em); marginBottom(0.5.em) } }) {}
                    Div({ style { height(16.px); width(40.percent); backgroundColor(Color("#333333")); borderRadius(0.375.em) } }) {}
                }
            }
        }
        return
    }

    error?.let { msg ->
        Div({
            style {
                backgroundColor(Color("#2a0f0f"))
                color(Color("#b91c1c"))
                padding(1.em)
                borderRadius(0.75.em)
                marginBottom(1.em)
                fontWeight("600")
            }
        }) { Text(msg) }
        return
    }

    if (categories.isEmpty()) {
        P({ style { color(Color("#a0a0a0")) } }) { Text(WebStrings.t(WebStringKey.CATEGORIES_EMPTY)) }
        return
    }
    
    if (filteredCategories.isEmpty()) {
        Div({
            style {
                textAlign("center")
                padding(3.em)
                backgroundColor(ThemeColors.surface)
                borderRadius(1.em)
            }
        }) {
            P({ style { color(ThemeColors.textSecondary); fontSize(1.1.em) } }) {
                Text(WebStrings.t(WebStringKey.CATEGORIES_NO_MATCH))
            }
            Button({
                style {
                    marginTop(1.em)
                    backgroundColor(ThemeColors.primary)
                    color(ThemeColors.textOnGold)
                    padding(0.5.em, 1.em)
                    borderRadius(0.5.em)
                    border(0.px)
                    cursor("pointer")
                }
                onClick {
                    selectedFilter = "All"
                }
            }) {
                Text(WebStrings.t(WebStringKey.CATEGORIES_CLEAR_FILTERS))
            }
        }
        return
    }

    // Results count
    P({
        style {
            color(ThemeColors.textSecondary)
            fontSize(0.9.em)
            marginBottom(0.75.em)
        }
    }) {
        Text(
            WebStrings.format(
                WebStringKey.CATEGORIES_SHOWING_COUNT,
                filteredCategories.size.toString(),
                categories.size.toString()
            )
        )
    }

    // Category cards grid
    Div({
        style {
            display(DisplayStyle.Grid)
            gridTemplateColumns("repeat(auto-fill, minmax(260px, 1fr))")
            gap(1.em)
        }
    }) {
        filteredCategories.forEach { c ->
            val type = getCategoryType(c.name)
            val iconSvg = getCategoryIconSvg(type)
            val color = getCategoryColor(type)
            
            Div({
                style {
                    backgroundColor(ThemeColors.surface)
                    padding(1.25.em)
                    borderRadius(1.em)
                    property("border", "1px solid ${ThemeColors.border}")
                    property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
                    cursor("pointer")
                    property("transition", "all 0.2s ease")
                }
                onClick {
                    if (expandedCategory == c.name) {
                        expandedCategory = null
                    } else {
                        expandedCategory = c.name
                        expandedProducts = emptyList()
                        expandedError = null
                        if (canonicalCategoryKey(c.name) == "transfer") {
                            expandedLoading = false
                        } else {
                            fetchProductsForCategory(c.name)
                        }
                    }
                }
            }) {
                // Icon and count row
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(0.75.em)
                    }
                }) {
                    // Icon circle with SVG (uniform gray)
                    Div({
                        style {
                            width(48.px)
                            height(48.px)
                            borderRadius(0.75.em)
                            backgroundColor(Color("#E8E8E8"))
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            color(Color("#666666"))
                        }
                        ref { element ->
                            element.innerHTML = iconSvg
                            onDispose {}
                        }
                    }) {}

                    // Count badge (uniform gold)
                    Div({
                        style {
                            backgroundColor(ThemeColors.primary)
                            color(ThemeColors.textOnGold)
                            padding(0.25.em, 0.625.em)
                            borderRadius(1.em)
                            fontSize(0.8.em)
                            fontWeight("600")
                        }
                }) { Text(WebStrings.format(WebStringKey.ITEMS_COUNT, c.count.toString())) }
                }
                
                // Category name
                H3({ 
                    style { 
                        marginBottom(0.25.em)
                        color(ThemeColors.textPrimary)
                        fontWeight("700")
                    } 
                }) {
                    Text(categoryTitleFromName(c.name))
                }
                
                // Type label
                val typeLabelKey = when (type) {
                    "flight" -> WebStringKey.CATEGORIES_FILTER_FLIGHTS
                    "hotel" -> WebStringKey.CATEGORIES_FILTER_HOTELS
                    "car" -> WebStringKey.CATEGORIES_FILTER_CAR_RENTALS
                    "visa" -> WebStringKey.CATEGORIES_FILTER_VISA_SERVICES
                    "tour" -> WebStringKey.CATEGORIES_FILTER_TOURS
                    "transfer" -> WebStringKey.CATEGORIES_FILTER_TRANSFERS
                    else -> WebStringKey.CATEGORY_TYPE_OTHER
                }
                
                P({
                    style {
                        color(ThemeColors.primaryDark)
                        fontSize(0.85.em)
                        fontWeight("500")
                        property("text-transform", "capitalize")
                    }
                }) {
                    Text(WebStrings.t(typeLabelKey))
                }
            }
        }
    }

    // Expanded product panel (inline below the category grid)
    expandedCategory?.let { catName ->
        Div({
            style {
                marginTop(1.5.em)
                padding(1.5.em)
                backgroundColor(ThemeColors.surface)
                borderRadius(1.em)
                property("border", "1px solid ${ThemeColors.border}")
            }
        }) {
            // Header with title and close button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(1.em)
                }
            }) {
                H2({
                    style {
                        margin(0.px)
                        color(ThemeColors.textPrimary)
                        fontSize(1.4.em)
                        fontWeight("800")
                    }
                }) {
                    Text(categoryTitleFromName(catName))
                }
                Button({
                    style {
                        backgroundColor(ThemeColors.surfaceElevated)
                        color(ThemeColors.textSecondary)
                        padding(0.4.em, 0.8.em)
                        borderRadius(0.5.em)
                        border(0.px)
                        cursor("pointer")
                        fontWeight("600")
                        fontSize(0.9.em)
                    }
                    onClick { expandedCategory = null }
                }) {
                    Text("Close")
                }
            }

            if (canonicalCategoryKey(catName) == "transfer") {
                TransferEnquiryForm()
            } else if (expandedLoading) {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(auto-fill, minmax(280px, 1fr))")
                        gap(1.em)
                    }
                }) {
                    repeat(3) {
                        Div({
                            style {
                                backgroundColor(ThemeColors.skeleton)
                                height(200.px)
                                borderRadius(1.em)
                                property("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
                            }
                        }) {}
                    }
                }
            } else if (expandedError != null) {
                P({ style { color(ThemeColors.error); fontWeight("600") } }) { Text(expandedError!!) }
            } else if (expandedProducts.isEmpty()) {
                P({ style { color(ThemeColors.textSecondary) } }) { Text("No products found for this category.") }
            } else {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(auto-fill, minmax(280px, 1fr))")
                        gap(1.25.em)
                    }
                }) {
                    expandedProducts.forEach { p ->
                        val priceParts = listOfNotNull(
                            p.priceUsd?.let { "USD $it" },
                            p.priceUgx?.let { "UGX $it" }
                        )

                        Div({
                            style {
                                backgroundColor(ThemeColors.surfaceElevated)
                                borderRadius(1.em)
                                property("border", "1px solid ${ThemeColors.border}")
                                property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
                                overflow("hidden")
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                            }
                        }) {
                            // Image
                            p.thumbnail?.let { url ->
                                if (url.isNotBlank()) {
                                    Img(src = url, attrs = {
                                        style {
                                            width(100.percent)
                                            height(180.px)
                                            property("object-fit", "cover")
                                        }
                                    })
                                }
                            }

                            // Content
                            Div({
                                style {
                                    padding(1.25.em)
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    flex(1)
                                }
                            }) {
                                // Title
                                H3({
                                    style {
                                        marginBottom(0.5.em)
                                        color(ThemeColors.textPrimary)
                                        fontWeight("700")
                                        fontSize(1.15.em)
                                    }
                                }) {
                                    Text(p.title ?: "Item")
                                }

                                // Description
                                if (!p.description.isNullOrBlank()) {
                                    P({
                                        style {
                                            color(ThemeColors.textSecondary)
                                            lineHeight("1.5")
                                            marginBottom(1.em)
                                            fontSize(0.95.em)
                                            flex(1)
                                        }
                                    }) {
                                        Text(p.description)
                                    }
                                }

                                // Price
                                if (priceParts.isNotEmpty()) {
                                    Div({
                                        style {
                                            backgroundColor(ThemeColors.successBg)
                                            padding(0.75.em, 1.em)
                                            borderRadius(0.5.em)
                                            marginBottom(1.em)
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.Center)
                                            justifyContent(JustifyContent.SpaceBetween)
                                        }
                                    }) {
                                        Span({
                                            style {
                                                color(ThemeColors.success)
                                                fontWeight("500")
                                                fontSize(0.85.em)
                                            }
                                        }) { Text("Price") }
                                        Span({
                                            style {
                                                color(ThemeColors.success)
                                                fontWeight("700")
                                                fontSize(1.1.em)
                                            }
                                        }) { Text(priceParts.joinToString(" • ")) }
                                    }
                                }

                                // Book Now button
                                Button({
                                    style {
                                        width(100.percent)
                                        backgroundColor(ThemeColors.primary)
                                        color(ThemeColors.textOnGold)
                                        padding(0.875.em)
                                        borderRadius(0.75.em)
                                        border(0.px)
                                        cursor("pointer")
                                        fontWeight("700")
                                        fontSize(1.em)
                                    }
                                    onClick { onNavigate("/checkout?productId=${p.id}") }
                                }) {
                                    Text("Book Now")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CategoryProductsPage(
    categoryName: String,
    onNavigate: (String) -> Unit,
) {
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var products by remember { mutableStateOf(emptyList<WebProduct>()) }
    var lastUpdatedMs by remember { mutableStateOf<Double?>(null) }
    var inFlight by remember { mutableStateOf(false) }
    var refreshTick by remember { mutableStateOf(0) }
    var attemptedNames by remember { mutableStateOf(emptyList<String>()) }

    // Build a list of category name variations to try (exact + normalized)
    val nameVariations = remember(categoryName) {
        val base = categoryName.trim()
        val canonical = canonicalCategoryKey(base)
        listOfNotNull(
            base.ifBlank { null },
            base.replaceFirstChar { it.uppercase() }.ifBlank { null },
            base.lowercase().ifBlank { null },
            canonical.replaceFirstChar { it.uppercase() }.ifBlank { null },
            canonical.lowercase().ifBlank { null },
            // Common Firebase suffix mappings
            if (canonical == "flight") "flightOffers" else null,
            if (canonical == "hotel") "Hotel" else null,
            if (canonical == "hotel") "hotel" else null,
            if (canonical == "car") "Car Rental" else null,
            if (canonical == "car") "car_rental" else null,
        ).distinct()
    }

    fun fetchWithCategoryName(nameToTry: String, showSpinner: Boolean, onComplete: () -> Unit) {
        if (nameToTry.isBlank()) { onComplete(); return }
        if (inFlight) { onComplete(); return }
        inFlight = true
        if (showSpinner) loading = true
        error = null

        val bodyJson = buildString {
            append("{")
            append("\"category\":\"${nameToTry.trim()}\",")
            append("\"limit\":50")
            append("}")
        }

        window.fetch(
            "$FUNCTIONS_BASE/listProductsByCategory",
            RequestInit(
                method = "POST",
                headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                body = bodyJson
            )
        ).then { resp ->
            resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val ok = (obj.success as Boolean?) == true
                    if (!ok) {
                        error = (obj.message as String?) ?: "Failed to load products"
                    } else {
                        val arr = (obj.products as Array<dynamic>?) ?: emptyArray()
                        val next = arr.map { d ->
                            WebProduct(
                                id = d.id as String? ?: "",
                                title = d.title as String?,
                                description = d.description as String?,
                                thumbnail = d.thumbnail as String?,
                                category = d.category as String?,
                                createdAt = (d.createdAt as Double?)?.toLong(),
                                priceUsd = (d.priceUsd as Double?)?.toInt(),
                                priceUgx = (d.priceUgx as Double?)?.toInt(),
                            )
                        }.filter { it.id.isNotBlank() }

                        val oldKey = products.joinToString("|") { it.id }
                        val newKey = next.joinToString("|") { it.id }
                        if (oldKey != newKey) {
                            products = next
                        }
                    }
                } catch (t: dynamic) {
                    error = "Invalid response from server"
                } finally {
                    loading = false
                    lastUpdatedMs = js("Date.now()") as Double
                    inFlight = false
                    onComplete()
                }
            }
        }.catch { _ ->
            error = "Network error (is the Cloud Function deployed?)"
            loading = false
            lastUpdatedMs = js("Date.now()") as Double
            inFlight = false
            onComplete()
        }
    }

    fun refresh(showSpinner: Boolean) {
        if (categoryName.isBlank()) return
        products = emptyList()
        attemptedNames = emptyList()

        fun tryNext(remaining: List<String>) {
            if (remaining.isEmpty()) {
                loading = false
                return
            }
            val head = remaining.first()
            val tail = remaining.drop(1)
            attemptedNames = attemptedNames + head
            fetchWithCategoryName(head, showSpinner && remaining == nameVariations) {
                if (products.isEmpty() && tail.isNotEmpty()) {
                    tryNext(tail)
                } else {
                    loading = false
                }
            }
        }

        tryNext(nameVariations)
    }

    LaunchedEffect(categoryName, refreshTick) {
        refresh(showSpinner = true)
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.75.em)
            marginBottom(1.em)
        }
    }) {
        Button({
            style {
                backgroundColor(ThemeColors.surface)
                color(ThemeColors.textPrimary)
                padding(0.65.em, 1.em)
                borderRadius(0.75.em)
                property("border", "1px solid ${ThemeColors.border}")
                cursor("pointer")
                fontWeight("700")
            }
            onClick { onNavigate("/categories") }
        }) { Text("Back") }

        H1({
            style {
                fontSize(2.0.em)
                fontWeight("800")
                margin(0.px)
                color(Color("#ffffff"))
            }
        }) { Text(categoryTitleFromName(categoryName)) }
    }

    P({ style { color(ThemeColors.textSecondary); marginBottom(1.25.em) } }) {
        Text("Category: ${categoryName.trim()} | Tried: ${attemptedNames.joinToString(", ")}")
    }

    PageHeaderRow(
        title = "Products",
        lastUpdatedMs = lastUpdatedMs,
        onRefresh = { refreshTick++ }
    )

    if (loading) {
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("repeat(auto-fill, minmax(280px, 1fr))")
                gap(1.em)
                marginTop(1.em)
            }
        }) {
            repeat(4) {
                Div({
                    style {
                        backgroundColor(ThemeColors.skeleton)
                        height(200.px)
                        borderRadius(1.em)
                        property("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
                    }
                }) {}
            }
        }
        return
    }

    error?.let { msg ->
        P({ style { color(ThemeColors.error); fontWeight("600") } }) { Text(msg) }
        return
    }

    if (products.isEmpty()) {
        Div({
            style {
                padding(2.em)
                backgroundColor(ThemeColors.surface)
                borderRadius(1.em)
                property("border", "1px solid ${ThemeColors.border}")
            }
        }) {
            P({ style { color(ThemeColors.textSecondary); fontWeight("600") } }) {
                Text("No products found for this category.")
            }
            P({ style { color(ThemeColors.textMuted); fontSize(0.9.em); marginTop(0.5.em) } }) {
                Text("Attempted category keys: ${attemptedNames.joinToString(", ")}. Make sure products in Firestore have a matching 'category' field.")
            }
        }
        return
    }

    // Enhanced product cards with Book Now
    Div({
        style {
            display(DisplayStyle.Grid)
            gridTemplateColumns("repeat(auto-fill, minmax(320px, 1fr))")
            gap(1.25.em)
        }
    }) {
        products.forEach { p ->
            val priceParts = listOfNotNull(
                p.priceUsd?.let { "USD $it" },
                p.priceUgx?.let { "UGX $it" }
            )
            
            Div({
                style {
                    backgroundColor(ThemeColors.surface)
                    borderRadius(1.em)
                    property("border", "1px solid ${ThemeColors.border}")
                    property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
                    overflow("hidden")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) {
                // Image
                p.thumbnail?.let { url ->
                    if (url.isNotBlank()) {
                        Img(src = url, attrs = {
                            style {
                                width(100.percent)
                                height(200.px)
                                property("object-fit", "cover")
                            }
                        })
                    }
                }
                
                // Content
                Div({
                    style {
                        padding(1.25.em)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        flex(1)
                    }
                }) {
                    // Title
                    H3({ 
                        style { 
                            marginBottom(0.5.em)
                            color(ThemeColors.textPrimary)
                            fontWeight("700")
                            fontSize(1.15.em)
                        } 
                    }) {
                        Text(p.title ?: "Item")
                    }
                    
                    // Description (full, not truncated)
                    if (!p.description.isNullOrBlank()) {
                        P({ 
                            style { 
                                color(ThemeColors.textSecondary)
                                lineHeight("1.5")
                                marginBottom(1.em)
                                fontSize(0.95.em)
                                flex(1)
                            } 
                        }) { 
                            Text(p.description) 
                        }
                    }
                    
                    // Price
                    if (priceParts.isNotEmpty()) {
                        Div({
                            style {
                                backgroundColor(ThemeColors.successBg)
                                padding(0.75.em, 1.em)
                                borderRadius(0.5.em)
                                marginBottom(1.em)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.SpaceBetween)
                            }
                        }) {
                            Span({
                                style {
                                    color(ThemeColors.success)
                                    fontWeight("500")
                                    fontSize(0.85.em)
                                }
                            }) { Text("Price") }
                            Span({
                                style {
                                    color(ThemeColors.success)
                                    fontWeight("700")
                                    fontSize(1.1.em)
                                }
                            }) { Text(priceParts.joinToString(" • ")) }
                        }
                    }
                    
                    // Book Now button
                    Button({
                        style {
                            width(100.percent)
                            backgroundColor(ThemeColors.primary)
                            color(ThemeColors.textOnGold)
                            padding(0.875.em)
                            borderRadius(0.75.em)
                            border(0.px)
                            cursor("pointer")
                            fontWeight("700")
                            fontSize(1.em)
                            property("box-shadow", "0 2px 8px rgba(37,99,235,0.25)")
                        }
                        onClick { onNavigate("/checkout?productId=${p.id}") }
                    }) {
                        Text("Book Now")
                    }
                }
            }
        }
    }
}

private fun categoryTitleFromName(name: String): String {
    val trimmed = name.trim()
    if (trimmed.isEmpty()) return "Category"
    // Add friendly suffixes for plain travel category names
    return when (trimmed) {
        "Flight" -> "Flight Offers"
        "Hotel" -> "Hotels & Guest House"
        "Car" -> "Car Rentals"
        "Tour" -> "Tour Packages"
        "Transfer" -> "Airport Transfers"
        // Legacy fitness-supplement names mapped to travel categories
        "Creatine" -> "Hotels & Stays"
        "Protein" -> "Hotels & Stays"
        "Gainers" -> "Tour Packages"
        "PreWorkout" -> "Car Rentals"
        else -> trimmed
    }
}

/**
 * Normalizes any category name variation to a canonical key.
 * This ensures "flight", "Flight", "flightOffers", "Flights" all map to "flight".
 */
fun canonicalCategoryKey(name: String): String {
    val lower = name.trim().lowercase()
    return when {
        lower.contains("flight") || lower.contains("air") -> "flight"
        lower.contains("hotel") || lower.contains("stay") || lower.contains("accommodation")
            || lower.contains("guest house") || lower.contains("guesthouse")
            || lower == "creatine" || lower == "protein" -> "hotel"
        lower.contains("car") || lower.contains("rental") || lower.contains("hire")
            || lower == "preworkout" || lower == "pre workout" || lower == "pre-workout" -> "car"
        lower.contains("visa") -> "visa"
        lower.contains("tour") || lower.contains("package")
            || lower == "gainers" -> "tour"
        lower.contains("transfer") || lower.contains("airport") || lower.contains("shuttle") -> "transfer"
        else -> lower.replace(" ", "_").replace("-", "_")
    }
}

@Composable
private fun PlaceholderPage(title: String, subtitle: String) {
    H1({
        style {
            fontSize(2.2.em)
            fontWeight("800")
            marginBottom(0.4.em)
            color(Color("#ffffff"))
        }
    }) { Text(title) }

    P({
        style {
            fontSize(1.05.em)
            color(Color("#a0a0a0"))
            marginBottom(1.25.em)
        }
    }) { Text(subtitle) }
}

/* ---------------- HERO ---------------- */

@Composable
fun HeroSection() {
    H1({
        style {
            fontSize(2.6.em)
            fontWeight("800")
            marginBottom(0.5.em)
            color(Color("#ffffff"))
        }
    }) {
        Text("Compare flight deals from hundreds of sites")
    }

    P({
        style {
            fontSize(1.1.em)
            color(Color("#a0a0a0"))
            marginBottom(1.5.em)
        }
    }) {
        Text("Search flights, stays, cars and visas — all in one place.")
    }
}

/* ---------------- SEARCH CARD ---------------- */

@Composable
fun SearchCard() {
    Div({
        style {
            backgroundColor(ThemeColors.surface)
            padding(1.75.em)
            borderRadius(1.em)
            property("box-shadow", "0 6px 18px rgba(0, 0, 0, 0.08)")
            marginBottom(2.em)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr 1fr 1fr auto")
                gap(1.em)
                alignItems(AlignItems.Center)
            }
        }) {
            InputBox("From", "Kampala (EBB)")
            InputBox("To", "Nairobi (NBO)")
            InputBox("Departure", "13 Dec")
            InputBox("Return", "16 Dec")

            Button({
                style {
                    backgroundColor(ThemeColors.primary)
                    color(ThemeColors.textOnGold)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    property("box-shadow", "0 4px 12px rgba(14,165,233,0.35)")
                }
            }) {
                Text(WebStrings.t(WebStringKey.FLIGHT_SEARCH))
            }
        }

        Br()

        Text(WebStrings.t(WebStringKey.TRAVELERS_ECONOMY_CURRENCY))
    }
}

@Composable
fun FlightSearchCard() {
    var origin by remember { mutableStateOf("EBB") }
    var destination by remember { mutableStateOf("NBO") }
    var departureDate by remember { mutableStateOf("2026-12-13") }
    var returnDate by remember { mutableStateOf("") }
    var adults by remember { mutableStateOf("1") }

    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var results by remember { mutableStateOf(emptyList<WebFlight>()) }

    Div({
        style {
            backgroundColor(Color("#000000"))
            padding(1.75.em)
            borderRadius(1.em)
            property("box-shadow", "0 6px 18px rgba(0, 0, 0, 0.08)")
            marginBottom(2.em)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr 1fr 1fr 0.7fr auto")
                gap(1.em)
                alignItems(AlignItems.End)
            }
        }) {
            TextInputBox("From (IATA)", origin) { origin = it }
            TextInputBox("To (IATA)", destination) { destination = it }
            TextInputBox("Departure (YYYY-MM-DD)", departureDate) { departureDate = it }
            TextInputBox("Return (optional)", returnDate) { returnDate = it }
            TextInputBox("Adults", adults) { adults = it }

            Button({
                style {
                    backgroundColor(Color("#d4af37"))
                    color(Color.white)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    property("min-width", "120px")
                    property("box-shadow", "0 4px 12px rgba(14,165,233,0.35)")
                }
                onClick {
                    if (loading) return@onClick
                    loading = true
                    error = null
                    results = emptyList()

                    val bodyJson = buildString {
                        append("{")
                        append("\"origin\":\"${origin.trim()}\",")
                        append("\"destination\":\"${destination.trim()}\",")
                        append("\"departureDate\":\"${departureDate.trim()}\",")
                        if (returnDate.trim().isNotEmpty()) {
                            append("\"returnDate\":\"${returnDate.trim()}\",")
                        }
                        append("\"adults\":${adults.trim().toIntOrNull() ?: 1}")
                        append("}")
                    }

                    window.fetch(
                        "$FUNCTIONS_BASE/searchFlights",
                        RequestInit(
                            method = "POST",
                            headers = Headers().also { h ->
                                h.append("Content-Type", "application/json")
                            },
                            body = bodyJson
                        )
                    ).then { resp ->
                        resp.text().then { text ->
                            try {
                                val obj = kotlin.js.JSON.parse<dynamic>(text)
                                val ok = (obj.success as Boolean?) == true
                                if (!ok) {
                                    error = (obj.message as String?) ?: "Failed to search flights"
                                } else {
                                    val arr = (obj.flights as Array<dynamic>?) ?: emptyArray()
                                    results = arr.map { d ->
                                        WebFlight(
                                            id = d.id as String? ?: "",
                                            airlineCode = d.airlineCode as String? ?: "",
                                            flightNumber = d.flightNumber as String? ?: "",
                                            origin = d.origin as String? ?: "",
                                            destination = d.destination as String? ?: "",
                                            departureIso = d.departureIso as String? ?: "",
                                            arrivalIso = d.arrivalIso as String? ?: "",
                                            duration = d.duration as String? ?: "",
                                            stops = (d.stops as Int?) ?: 0,
                                            priceTotal = d.priceTotal as String?,
                                            currency = d.currency as String?
                                        )
                                    }
                                }
                            } catch (t: dynamic) {
                                error = "Invalid response from server"
                            } finally {
                                loading = false
                            }
                        }
                    }.catch { _ ->
                        error = "Network error (is the Cloud Function deployed?)"
                        loading = false
                    }
                }
            }) {
                Text(if (loading) WebStrings.t(WebStringKey.PLEASE_WAIT) else WebStrings.t(WebStringKey.FLIGHT_SEARCH))
            }
        }

        Br()
        P({ style { color(ThemeColors.textSecondary) } }) {
            Text("Tip: Use airport IATA codes like EBB, NBO, LHR. This web search is safe (API keys stay on the server).")
        }

        error?.let { msg ->
            Br()
            P({
                style {
                    color(ThemeColors.error)
                    fontWeight("600")
                }
            }) { Text(msg) }
        }

        if (results.isNotEmpty()) {
            Br()
            H2({
                style {
                    fontSize(1.4.em)
                    marginBottom(0.75.em)
                }
            }) { Text("Results (${results.size})") }

            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fit, minmax(280px, 1fr))")
                    gap(1.em)
                }
            }) {
                results.forEach { f ->
                    Div({
                        style {
                            backgroundColor(ThemeColors.surface)
                            padding(1.em)
                            borderRadius(0.9.em)
                            property("border", "1px solid ${ThemeColors.border}")
                            property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
                        }
                    }) {
                        H3({
                            style {
                                marginBottom(0.25.em)
                                color(ThemeColors.textPrimary)
                            }
                        }) { Text("${f.origin} → ${f.destination}") }

                        P({ style { color(ThemeColors.textSecondary) } }) {
                            Text("${f.airlineCode} ${f.flightNumber} • Stops: ${f.stops} • ${f.duration}")
                        }
                        P({ style { color(ThemeColors.textSecondary) } }) {
                            Text("Dep: ${f.departureIso}")
                        }
                        P({ style { color(ThemeColors.textSecondary) } }) {
                            Text("Arr: ${f.arrivalIso}")
                        }
                        f.priceTotal?.let { p ->
                            P({
                                style {
                                    marginTop(0.5.em)
                                    color(ThemeColors.primary)
                                    fontWeight("700")
                                }
                            }) { Text("${f.currency ?: ""} $p") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun HotelSearchCard() {
    var cityQuery by remember { mutableStateOf("London") }
    var arrivalDate by remember { mutableStateOf("2026-12-13") }
    var departureDate by remember { mutableStateOf("2026-12-16") }
    var adults by remember { mutableStateOf("2") }
    var rooms by remember { mutableStateOf("1") }

    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var results by remember { mutableStateOf(emptyList<WebHotel>()) }
    var searchedOnce by remember { mutableStateOf(false) }

    Div({
        style {
            backgroundColor(Color("#000000"))
            padding(1.75.em)
            borderRadius(1.em)
            property("box-shadow", "0 6px 18px rgba(0, 0, 0, 0.08)")
            marginBottom(2.em)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1.4fr 1fr 1fr 0.7fr 0.7fr auto")
                gap(1.em)
                alignItems(AlignItems.End)
            }
        }) {
            TextInputBox("City", cityQuery) { cityQuery = it }
            TextInputBox("Check-in (YYYY-MM-DD)", arrivalDate) { arrivalDate = it }
            TextInputBox("Check-out (YYYY-MM-DD)", departureDate) { departureDate = it }
            TextInputBox("Adults", adults) { adults = it }
            TextInputBox("Rooms", rooms) { rooms = it }

            Button({
                style {
                    backgroundColor(Color("#d4af37"))
                    color(Color.white)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    property("min-width", "120px")
                }
                onClick {
                    if (loading) return@onClick
                    loading = true
                    error = null
                    results = emptyList()
                    searchedOnce = true

                    val bodyJson = buildString {
                        append("{")
                        append("\"query\":\"${cityQuery.trim()}\",")
                        append("\"arrivalDate\":\"${arrivalDate.trim()}\",")
                        append("\"departureDate\":\"${departureDate.trim()}\",")
                        append("\"adults\":${adults.trim().toIntOrNull() ?: 2},")
                        append("\"rooms\":${rooms.trim().toIntOrNull() ?: 1},")
                        append("\"currencyCode\":\"USD\"")
                        append("}")
                    }

                    window.fetch(
                        "$FUNCTIONS_BASE/searchHotels",
                        RequestInit(
                            method = "POST",
                            headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                            body = bodyJson
                        )
                    ).then { resp ->
                        resp.text().then { text ->
                            try {
                                val obj = kotlin.js.JSON.parse<dynamic>(text)
                                val ok = (obj.success as Boolean?) == true
                                if (!ok) {
                                    error = (obj.message as String?) ?: "Failed to search hotels"
                                } else {
                                    val arr = (obj.hotels as Array<dynamic>?) ?: emptyArray()
                                    results = arr.map { d ->
                                        WebHotel(
                                            id = d.id as String? ?: "",
                                            name = d.name as String?,
                                            city = d.city as String?,
                                            countryCode = d.countryCode as String?,
                                            imageUrl = d.imageUrl as String?,
                                            reviewScore = d.reviewScore as Double?,
                                            reviewCount = d.reviewCount as Int?,
                                            price = d.price as Double?,
                                            currency = d.currency as String?
                                        )
                                    }
                                }
                            } catch (t: dynamic) {
                                error = "Invalid response from server"
                            } finally {
                                loading = false
                            }
                        }
                    }.catch { _ ->
                        error = "Network error (is the Cloud Function deployed?)"
                        loading = false
                    }
                }
            }) { Text(if (loading) WebStrings.t(WebStringKey.PLEASE_WAIT) else WebStrings.t(WebStringKey.FLIGHT_SEARCH)) }
        }

        Br()
        Button({
            style {
                backgroundColor(Color("#000000"))
                color(Color.white)
                padding(0.75.em, 1.25.em)
                borderRadius(0.75.em)
                border(0.px)
                cursor("pointer")
                fontWeight("700")
            }
            onClick {
                val url = BookingLinks.hotelSearch(
                    destination = cityQuery.trim().ifEmpty { "Hotels" },
                    checkIn = arrivalDate.trim(),
                    checkOut = departureDate.trim(),
                    adults = adults.trim().toIntOrNull() ?: 2,
                    rooms = rooms.trim().toIntOrNull() ?: 1,
                    currency = "USD"
                )
                window.open(url, "_blank")
            }
        }) { Text("Open on Booking.com") }

        error?.let { msg ->
            Br()
            P({
                style {
                    color(Color("#b91c1c"))
                    fontWeight("600")
                }
            }) { Text(msg) }
        }

        if (results.isNotEmpty()) {
            Br()
            H2({ style { fontSize(1.4.em); marginBottom(0.75.em) } }) { Text("Hotels (${results.size})") }

            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fit, minmax(280px, 1fr))")
                    gap(1.em)
                }
            }) {
                results.forEach { h ->
                    Div({
                        style {
                            backgroundColor(Color("#000000"))
                            padding(1.em)
                            borderRadius(0.9.em)
                            property("border", "1px solid #333333")
                            property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
                        }
                    }) {
                        H3({ style { marginBottom(0.25.em); color(Color("#ffffff")) } }) {
                            Text(h.name ?: "Hotel")
                        }
                        P({ style { color(Color("#a0a0a0")) } }) {
                            Text(listOfNotNull(h.city, h.countryCode).joinToString(" • "))
                        }
                        if (h.reviewScore != null) {
                            P({ style { color(Color("#a0a0a0")) } }) {
                                Text("Rating: ${h.reviewScore} (${h.reviewCount ?: 0} reviews)")
                            }
                        }
                        if (h.price != null) {
                            P({
                                style {
                                    marginTop(0.5.em)
                                    color(Color("#AE7930"))
                                    fontWeight("700")
                                }
                            }) { Text("${h.currency ?: ""} ${h.price}") }
                        }
                        h.imageUrl?.let { url ->
                            Br()
                            Img(src = url, attrs = {
                                style {
                                    width(100.percent)
                                    borderRadius(0.75.em)
                                    property("object-fit", "cover")
                                    property("max-height", "180px")
                                }
                            })
                        }
                    }
                }
            }
        }

        if (searchedOnce && !loading && error == null && results.isEmpty()) {
            Br()
            P({ style { color(Color("#a0a0a0")) } }) {
                Text("No hotels found for this search. Try a different city or dates.")
            }
        }
    }
}

@Composable
fun CarRentalSearchCard() {
    var pickupQuery by remember { mutableStateOf("New York") }
    var dropoffQuery by remember { mutableStateOf("") }
    var pickUpDate by remember { mutableStateOf("2026-01-02") }
    var dropOffDate by remember { mutableStateOf("2026-01-10") }
    var pickUpTime by remember { mutableStateOf("10:00") }
    var dropOffTime by remember { mutableStateOf("10:00") }
    var driverAge by remember { mutableStateOf("30") }

    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var results by remember { mutableStateOf(emptyList<WebCarRental>()) }
    var searchedOnce by remember { mutableStateOf(false) }

    Div({
        style {
            backgroundColor(Color("#000000"))
            padding(1.75.em)
            borderRadius(1.em)
            property("box-shadow", "0 6px 18px rgba(0, 0, 0, 0.08)")
            marginBottom(2.em)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1.2fr 1.2fr 1fr 1fr 0.8fr 0.8fr 0.7fr auto")
                gap(1.em)
                alignItems(AlignItems.End)
            }
        }) {
            TextInputBox("Pickup city", pickupQuery) { pickupQuery = it }
            TextInputBox("Dropoff city (optional)", dropoffQuery) { dropoffQuery = it }
            TextInputBox("Pickup date", pickUpDate) { pickUpDate = it }
            TextInputBox("Dropoff date", dropOffDate) { dropOffDate = it }
            TextInputBox("Pickup time (HH:mm)", pickUpTime) { pickUpTime = it }
            TextInputBox("Dropoff time (HH:mm)", dropOffTime) { dropOffTime = it }
            TextInputBox("Driver age", driverAge) { driverAge = it }

            Button({
                style {
                    backgroundColor(Color("#d4af37"))
                    color(Color.white)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    property("min-width", "120px")
                }
                onClick {
                    if (loading) return@onClick
                    loading = true
                    error = null
                    results = emptyList()
                    searchedOnce = true

                    val bodyJson = buildString {
                        append("{")
                        append("\"pickupQuery\":\"${pickupQuery.trim()}\",")
                        if (dropoffQuery.trim().isNotEmpty()) {
                            append("\"dropoffQuery\":\"${dropoffQuery.trim()}\",")
                        }
                        append("\"pickUpDate\":\"${pickUpDate.trim()}\",")
                        append("\"dropOffDate\":\"${dropOffDate.trim()}\",")
                        append("\"pickUpTime\":\"${pickUpTime.trim()}\",")
                        append("\"dropOffTime\":\"${dropOffTime.trim()}\",")
                        append("\"driverAge\":${driverAge.trim().toIntOrNull() ?: 30},")
                        append("\"currencyCode\":\"USD\"")
                        append("}")
                    }

                    window.fetch(
                        "$FUNCTIONS_BASE/searchCarRentals",
                        RequestInit(
                            method = "POST",
                            headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                            body = bodyJson
                        )
                    ).then { resp ->
                        resp.text().then { text ->
                            try {
                                val obj = kotlin.js.JSON.parse<dynamic>(text)
                                val ok = (obj.success as Boolean?) == true
                                if (!ok) {
                                    error = (obj.message as String?) ?: "Failed to search car rentals"
                                } else {
                                    val arr = (obj.cars as Array<dynamic>?) ?: emptyArray()
                                    results = arr.map { d ->
                                        WebCarRental(
                                            name = d.name as String?,
                                            type = d.type as String?,
                                            seats = d.seats as Int?,
                                            transmission = d.transmission as String?,
                                            fuel = d.fuel as String?,
                                            imageUrl = d.imageUrl as String?,
                                            price = d.price as Double?,
                                            currency = d.currency as String?,
                                            provider = d.provider as String?,
                                            pickupCity = d.pickupCity as String?,
                                            dropoffCity = d.dropoffCity as String?,
                                        )
                                    }
                                }
                            } catch (t: dynamic) {
                                error = "Invalid response from server"
                            } finally {
                                loading = false
                            }
                        }
                    }.catch { _ ->
                        error = "Network error (is the Cloud Function deployed?)"
                        loading = false
                    }
                }
            }) { Text(if (loading) WebStrings.t(WebStringKey.PLEASE_WAIT) else WebStrings.t(WebStringKey.FLIGHT_SEARCH)) }
        }

        Br()
        Button({
            style {
                backgroundColor(Color("#000000"))
                color(Color.white)
                padding(0.75.em, 1.25.em)
                borderRadius(0.75.em)
                border(0.px)
                cursor("pointer")
                fontWeight("700")
            }
            onClick {
                val url = BookingLinks.carSearch(
                    locationQuery = pickupQuery.trim().ifEmpty { "Car rentals" },
                    pickUpDate = pickUpDate.trim(),
                    pickUpTime = pickUpTime.trim(),
                    dropOffDate = dropOffDate.trim(),
                    dropOffTime = dropOffTime.trim(),
                    driverAge = driverAge.trim().toIntOrNull() ?: 30,
                    currency = "USD"
                )
                window.open(url, "_blank")
            }
        }) { Text("Open on Booking.com") }

        error?.let { msg ->
            Br()
            P({ style { color(Color("#b91c1c")); fontWeight("600") } }) { Text(msg) }
        }

        if (results.isNotEmpty()) {
            Br()
            H2({ style { fontSize(1.4.em); marginBottom(0.75.em) } }) { Text("Cars (${results.size})") }

            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fit, minmax(280px, 1fr))")
                    gap(1.em)
                }
            }) {
                results.forEach { c ->
                    Div({
                        style {
                            backgroundColor(Color("#000000"))
                            padding(1.em)
                            borderRadius(0.9.em)
                            property("border", "1px solid #333333")
                            property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
                        }
                    }) {
                        H3({ style { marginBottom(0.25.em); color(Color("#ffffff")) } }) {
                            Text(c.name ?: "Car")
                        }
                        P({ style { color(Color("#a0a0a0")) } }) {
                            Text(listOfNotNull(c.type, c.transmission, c.fuel).joinToString(" • "))
                        }
                        P({ style { color(Color("#a0a0a0")) } }) {
                            Text(listOfNotNull(c.provider, c.pickupCity, c.dropoffCity).joinToString(" • "))
                        }
                        if (c.price != null) {
                            P({ style { marginTop(0.5.em); color(Color("#AE7930")); fontWeight("700") } }) {
                                Text("${c.currency ?: ""} ${c.price}")
                            }
                        }
                        c.imageUrl?.let { url ->
                            Br()
                            Img(src = url, attrs = {
                                style {
                                    width(100.percent)
                                    borderRadius(0.75.em)
                                    property("object-fit", "cover")
                                    property("max-height", "180px")
                                }
                            })
                        }
                    }
                }
            }
        }

        if (searchedOnce && !loading && error == null && results.isEmpty()) {
            Br()
            P({ style { color(Color("#a0a0a0")) } }) {
                Text("No cars found for this search. Try an airport (e.g., \"JFK\", \"LHR\"), different dates, or a major city.")
            }
        }
    }
}

private data class WebFlight(
    val id: String,
    val airlineCode: String,
    val flightNumber: String,
    val origin: String,
    val destination: String,
    val departureIso: String,
    val arrivalIso: String,
    val duration: String,
    val stops: Int,
    val priceTotal: String?,
    val currency: String?,
)

private data class WebHotel(
    val id: String,
    val name: String?,
    val city: String?,
    val countryCode: String?,
    val imageUrl: String?,
    val reviewScore: Double?,
    val reviewCount: Int?,
    val price: Double?,
    val currency: String?,
)

private data class WebCarRental(
    val name: String?,
    val type: String?,
    val seats: Int?,
    val transmission: String?,
    val fuel: String?,
    val imageUrl: String?,
    val price: Double?,
    val currency: String?,
    val provider: String?,
    val pickupCity: String?,
    val dropoffCity: String?,
)

private data class WebCategory(
    val name: String,
    val count: Int,
)

private data class WebProduct(
    val id: String,
    val title: String?,
    val description: String?,
    val thumbnail: String?,
    val category: String?,
    val createdAt: Long?,
    val priceUsd: Int?,
    val priceUgx: Int?,
)

@Composable
private fun TextInputBox(label: String, value: String, onValueChange: (String) -> Unit) {
    Div {
        Label {
            Span({
                style {
                    fontSize(0.8.em)
                    color(ThemeColors.textSecondary)
                }
            }) { Text(label) }
        }
        Input(type = InputType.Text, attrs = {
            this.value(value)
            onInput { onValueChange(it.value) }
            style {
                width(100.percent)
                padding(0.75.em)
                borderRadius(0.5.em)
                property("border", "1px solid ${ThemeColors.border}")
                backgroundColor(ThemeColors.surface)
                fontWeight("600")
                color(ThemeColors.textPrimary)
            }
        })
    }
}

@Composable
private fun ValidatedInputBox(
    label: String,
    value: String,
    showError: Boolean,
    errorMessage: String,
    onValueChange: (String) -> Unit
) {
    Div {
        Label {
            Span({
                style {
                    fontSize(0.8.em)
                    color(ThemeColors.textSecondary)
                }
            }) { Text(label) }
        }
        Input(type = InputType.Text, attrs = {
            this.value(value)
            onInput { onValueChange(it.value) }
            style {
                width(100.percent)
                padding(0.75.em)
                borderRadius(0.5.em)
                property(
                    "border",
                    if (showError) "1.5px solid ${ThemeColors.error}" else "1px solid ${ThemeColors.border}"
                )
                backgroundColor(if (showError) ThemeColors.errorBg else ThemeColors.surface)
                fontWeight("600")
                color(ThemeColors.textPrimary)
                property("outline", "none")
                property("transition", "border 0.15s ease, background-color 0.15s ease")
            }
        })
        if (showError) {
            P({
                style {
                    color(ThemeColors.error)
                    fontSize(0.75.em)
                    marginTop(0.3.em)
                    fontWeight("500")
                }
            }) { Text(errorMessage) }
        }
    }
}

@Composable
fun InputBox(label: String, value: String) {
    Div {
        Label {
            Span({
                style {
                    fontSize(0.8.em)
                    color(Color("#a0a0a0"))
                }
            }) { Text(label) }
        }

        Div({
            style {
                padding(0.75.em)
                borderRadius(0.5.em)
                property("border", "1px solid #444444")
                backgroundColor(Color("#000000"))
                fontWeight("600")
            }
        }) {
            Text(value)
        }
    }
}

/* ---------------- OFFERS ---------------- */

@Composable
fun OffersSection() {
    H2({
        style {
            marginBottom(1.em)
            fontSize(1.6.em)
        }
    }) {
        Text("Special Flight Offers ✈️")
    }

    Div({
        style {
            display(DisplayStyle.Grid)
            gridTemplateColumns("repeat(auto-fit, minmax(240px, 1fr))")
            gap(1.5.em)
        }
    }) {
        OfferCard("Weekend Escape", "Save big this weekend", "$1000")
        OfferCard("Holiday Deals", "Limited time offer", "$3990")
    }
}

@Composable
fun OfferCard(title: String, desc: String, price: String) {
    Div({
        style {
            backgroundColor(Color("#000000"))
            padding(1.25.em)
            borderRadius(1.em)
            property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.07)")
        }
    }) {
        H3 { Text(title) }
        P({ style { color(Color("#a0a0a0")) } }) { Text(desc) }
        P({
            style {
                color(Color("#AE7930"))
                fontWeight("700")
                fontSize(1.1.em)
            }
        }) {
            Text(price)
        }
    }
}

/* ---------------- TRANSFER ENQUIRY FORM ---------------- */

@Composable
private fun TransferEnquiryForm() {
    var isReturn by remember { mutableStateOf(false) }
    var pickup by remember { mutableStateOf("") }
    var destination by remember { mutableStateOf("") }
    var date by remember { mutableStateOf("") }
    var returnDate by remember { mutableStateOf("") }
    var passengers by remember { mutableStateOf("2") }
    var attemptedSend by remember { mutableStateOf(false) }

    // Validation helpers
    val dateRegex = Regex("^\\d{4}-\\d{2}-\\d{2}$")
    val isPickupValid = pickup.isNotBlank()
    val isDestinationValid = destination.isNotBlank()
    val isDateValid = date.isNotBlank() && dateRegex.matches(date)
    val isReturnDateValid = !isReturn || (returnDate.isNotBlank() && dateRegex.matches(returnDate))
    val isPassengersValid = passengers.toIntOrNull()?.let { it > 0 } ?: false
    val allValid = isPickupValid && isDestinationValid && isDateValid && isReturnDateValid && isPassengersValid

    Div({
        style {
            backgroundColor(ThemeColors.surface)
            padding(1.75.em)
            borderRadius(1.em)
            property("border", "1px solid ${ThemeColors.border}")
            property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
            maxWidth(680.px)
            property("margin", "0 auto")
        }
    }) {
        H2({
            style {
                fontSize(1.6.em)
                fontWeight("800")
                marginBottom(0.25.em)
                color(ThemeColors.textPrimary)
            }
        }) { Text("Book your airport taxi") }

        P({
            style {
                fontSize(1.em)
                color(ThemeColors.textSecondary)
                marginBottom(1.25.em)
            }
        }) { Text("Easy airport transfers to and from your accommodation") }

        // One-way / Return toggle
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(1.5.em)
                marginBottom(1.em)
            }
        }) {
            RadioOption("One-way", selected = !isReturn) { isReturn = false }
            RadioOption("Return", selected = isReturn) { isReturn = true }
        }

        // Form fields
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr")
                gap(1.em)
                marginBottom(0.5.em)
                property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
            }
        }) {
            ValidatedInputBox("Enter pick-up location", pickup, attemptedSend && !isPickupValid, "Pick-up location is required") { pickup = it }
            ValidatedInputBox("Enter destination", destination, attemptedSend && !isDestinationValid, "Destination is required") { destination = it }
            ValidatedInputBox("Date (YYYY-MM-DD)", date, attemptedSend && !isDateValid, "Enter a valid date (YYYY-MM-DD)") { date = it }
            if (isReturn) {
                ValidatedInputBox("Return date (YYYY-MM-DD)", returnDate, attemptedSend && !isReturnDateValid, "Enter a valid return date (YYYY-MM-DD)") { returnDate = it }
            }
            ValidatedInputBox("Passengers", passengers, attemptedSend && !isPassengersValid, "Enter a valid number of passengers") { passengers = it }
        }

        // Validation summary
        if (attemptedSend && !allValid) {
            P({
                style {
                    color(ThemeColors.error)
                    fontSize(0.9.em)
                    marginBottom(0.75.em)
                    fontWeight("600")
                }
            }) { Text("Please fill in all required fields correctly before sending.") }
        }

        // Send button
        Button({
            style {
                width(100.percent)
                backgroundColor(if (allValid) ThemeColors.primary else ThemeColors.primaryMuted)
                color(if (allValid) ThemeColors.textOnGold else ThemeColors.textSecondary)
                padding(0.9.em)
                borderRadius(0.75.em)
                border(0.px)
                cursor(if (allValid) "pointer" else "not-allowed")
                fontWeight("700")
                fontSize(1.05.em)
                property("box-shadow", if (allValid) "0 4px 12px rgba(0,0,0,0.15)" else "none")
                property("transition", "all 0.15s ease")
            }
            onClick {
                attemptedSend = true
                if (!allValid) return@onClick
                val msg = buildString {
                    append("*Airport Transfer Request*")
                    append("%0A%0APick-up: ")
                    append(js("encodeURIComponent")(pickup) as String)
                    append("%0ADestination: ")
                    append(js("encodeURIComponent")(destination) as String)
                    append("%0ADate: ")
                    append(js("encodeURIComponent")(date) as String)
                    if (isReturn && returnDate.isNotBlank()) {
                        append("%0AReturn date: ")
                        append(js("encodeURIComponent")(returnDate) as String)
                    }
                    append("%0ATrip: ")
                    append(if (isReturn) "Return" else "One-way")
                    append("%0APassengers: ")
                    append(passengers)
                    append("%0A%0ASent from FYAT Travel web portal")
                }
                window.open("https://wa.me/256758250625?text=$msg", "_blank")
            }
        }) { Text("Send") }
    }
}

@Composable
private fun RadioOption(label: String, selected: Boolean, onClick: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.5.em)
            cursor("pointer")
        }
        onClick { onClick() }
    }) {
        Div({
            style {
                width(18.px)
                height(18.px)
                borderRadius(50.percent)
                property("border", if (selected) "5px solid ${ThemeColors.primary}" else "2px solid ${ThemeColors.border}")
                backgroundColor(if (selected) ThemeColors.background else Color.transparent)
                property("transition", "border 0.15s ease")
                property("flex-shrink", "0")
            }
        }) {}
        Span({
            style {
                fontSize(0.95.em)
                color(if (selected) ThemeColors.textPrimary else ThemeColors.textSecondary)
                fontWeight(if (selected) "600" else "400")
            }
        }) { Text(label) }
    }
}
